import React, {useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {Modal, ModalBody, ModalHeader} from 'reactstrap';
import {IJourney} from 'app/shared/model/journey.model';
import {IRootState} from 'app/shared/reducers';
import {IFollowup} from 'app/shared/model/followup.model';
import {ISymptom} from 'app/shared/model/symptom.model';
import {ICondition} from 'app/shared/model/condition.model';
import {IPatientBaby} from 'app/shared/model/patient-baby.model';
import {CaseType} from 'app/shared/model/enumerations/case-type.model';
import {getEntities as getFollowups} from 'app/entities/followup/followup.reducer';
import {getEntities as getSymptoms} from 'app/entities/symptom/symptom.reducer';
import {getEntities as getConditions} from 'app/entities/condition/condition.reducer';

// Import step components
import {
  AddAnotherBabyStep,
  AdditionalDetailsStep,
  BabyAliveStep,
  BabyFollowupConditionsStep,
  BabyFollowupOutcomesStep,
  BabyInfoStep,
  BabySymptomsStep,
  DiagnosisAccuracyStep,
  MotherAliveStep,
  MotherDeliveredStep,
  MotherFollowupConditionsStep,
  MotherFollowupOutcomesStep,
  MotherSymptomsStep
} from './followup-steps';
import {EmergencyType} from "app/shared/model/enumerations/emergency-type.model";
import {IMMamaSettings} from "app/shared/model/mmama-settings-model";
import {FollowupType} from "app/shared/model/enumerations/followup-type.model";

// Constants
const MAX_BABIES = 10;

const STEPS = {
  DIAGNOSIS_ACCURACY: {number: 1, component: DiagnosisAccuracyStep},
  MOTHER_SYMPTOMS: {number: 2, component: MotherSymptomsStep},
  BABY_SYMPTOMS: {number: 3, component: BabySymptomsStep},
  MOTHER_ALIVE: {number: 4, component: MotherAliveStep},
  MOTHER_DELIVERED: {number: 5, component: MotherDeliveredStep},
  MOTHER_FOLLOWUP_OUTCOMES: {number: 6, component: MotherFollowupOutcomesStep},
  MOTHER_FOLLOWUP_CONDITIONS: {number: 7, component: MotherFollowupConditionsStep},
  BABY_ALIVE: {number: 8, component: BabyAliveStep},
  BABY_INFO: {number: 9, component: BabyInfoStep},
  BABY_FOLLOWUP_OUTCOMES: {number: 10, component: BabyFollowupOutcomesStep},
  BABY_FOLLOWUP_CONDITIONS: {number: 11, component: BabyFollowupConditionsStep},
  ADD_ANOTHER_BABY: {number: 12, component: AddAnotherBabyStep},
  ADDITIONAL_DETAILS: {number: 13, component: AdditionalDetailsStep},
} as const;

interface FollowupModalProps extends StateProps, DispatchProps {
  isOpen: boolean;
  toggle: () => void;
  journey: IJourney;
  countrySettings: IMMamaSettings;
  onSave: (followupData: FollowupFormData) => void;
}

export interface FollowupFormData {
  journeyId: string;
  diagnosisOk: boolean | null;
  motherSymptoms: string[];
  motherSymptom: string | null;
  babySymptoms: string[];
  babySymptom: string | null;
  motherAlive: boolean | null;
  isDelivered: boolean | null;
  motherFollowupOutcome: string | null;
  motherFollowupCondition: string | null;
  babyAlive: boolean | null;
  babyFollowupOutcome: string | null;
  babyFollowupCondition: string | null;
  patientBabies: IPatientBaby[];
  additionalDetails: string;
}

export interface StepComponentProps {
  formData: FollowupFormData;
  updateFormData: (updates: Partial<FollowupFormData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  canGoBack: boolean;
  canGoNext: boolean;
  patientCaseType: CaseType;
  journey: IJourney;
  motherFollowupData: IFollowup[];
  babyFollowupData: IFollowup[];
  motherSymptomsData: ISymptom[];
  babySymptomsData: ISymptom[];
  motherConditionsData: ICondition[];
  babyConditionsData: ICondition[];
  patientInfo?: any;
  onSave?: (formData: FollowupFormData) => void;
  updating?: boolean;
}

const FollowupModalComponent = (props: FollowupModalProps) => {
  const {
    isOpen,
    toggle,
    journey,
    countrySettings,
    onSave,
    followupList,
    symptomsList,
    conditionsList,
    followupSaving,
  } = props;
  const patientCaseType = journey.patientCase?.type || CaseType.MOTHER;
  const patientCountryId = journey.patientCase?.patient?.countryId;
  const patientInfo = journey.patientCase?.patient;

  // Determine emergency type for mother
  const motherEmergencyType = useMemo(() => {
    return journey.patientCase?.emergencyType || null;
  }, [journey.patientCase?.emergencyType]);

  const isAntepartum = motherEmergencyType === 'ANTEPARTUM';
  const isLabour = motherEmergencyType === 'LABOUR';
  const isPostpartum = motherEmergencyType === 'POSTPARTUM';
  const isNeonatal = motherEmergencyType === 'NEONATAL';

  const initialFormData: FollowupFormData = {
    journeyId: journey.id,
    diagnosisOk: null,
    motherSymptoms: [],
    motherSymptom: null,
    babySymptoms: [],
    babySymptom: null,
    motherAlive: null,
    isDelivered: isLabour || (isPostpartum && patientCaseType === CaseType.BOTH) ? true : null,
    motherFollowupOutcome: null,
    motherFollowupCondition: null,
    babyAlive: null,
    babyFollowupOutcome: null,
    babyFollowupCondition: null,
    patientBabies: patientInfo?.patientBabies ? [...patientInfo.patientBabies] : [],
    additionalDetails: '',
  }

  // Navigation state
  const [currentStep, setCurrentStep] = useState<number>(
    countrySettings?.enableConfirmDiagnosis
      ? STEPS.DIAGNOSIS_ACCURACY.number
      : patientCaseType === CaseType.MOTHER || patientCaseType === CaseType.BOTH
        ? STEPS.MOTHER_ALIVE.number
        : STEPS.BABY_ALIVE.number
  );
  const [navigationStack, setNavigationStack] = useState<number[]>([]);

  // Baby tracking state
  const [currentBabyIndex, setCurrentBabyIndex] = useState(0);

  // Form data state
  const [formData, setFormData] = useState<FollowupFormData>({...initialFormData});

  // Load data on mount
  useEffect(() => {
    if (isOpen && patientCountryId) {
      // Load followups if not already loaded
      if (!followupList || followupList.length === 0) {
        props.getFollowups();
      }

      // Load conditions if not already loaded
      if (!conditionsList || conditionsList.length === 0) {
        props.getConditions();
      }

      // Load symptoms if not already loaded
      if (!symptomsList || symptomsList.length === 0) {
        props.getSymptoms();
      }
    }
  }, [isOpen, patientCountryId]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(countrySettings?.enableConfirmDiagnosis
        ? STEPS.DIAGNOSIS_ACCURACY.number
        : patientCaseType === CaseType.MOTHER || patientCaseType === CaseType.BOTH
          ? STEPS.MOTHER_ALIVE.number
          : STEPS.BABY_ALIVE.number
      );
      setNavigationStack([]);
      setFormData({...initialFormData});
      setCurrentBabyIndex(0);
    }
  }, [isOpen, countrySettings?.enableConfirmDiagnosis]);

  // Filter data by country and emergency type
  const filteredMotherFollowups = useMemo(() => {
    return followupList?.filter(followup =>
      followup.countryId === patientCountryId &&
      followup.followupType === FollowupType.MATERNAL &&
      followup.emergencyType === motherEmergencyType &&
      followup.isActive &&
      !followup.isCondition // Only get followups, not conditions
    ) || [];
  }, [followupList, patientCountryId, motherEmergencyType]);

  const filteredBabyFollowups = useMemo(() => {
    return followupList?.filter(followup =>
      followup.countryId === patientCountryId &&
      followup.followupType === FollowupType.NEONATAL &&
      (motherEmergencyType !== EmergencyType.POSTPARTUM ? followup.emergencyType === motherEmergencyType : followup.emergencyType === EmergencyType.NEONATAL) &&
      followup.isActive &&
      !followup.isCondition // Only get followups, not conditions
    ) || [];
  }, [followupList, patientCountryId, motherEmergencyType]);

  const filteredMotherConditions = useMemo(() => {
    return conditionsList?.filter(condition =>
      condition.countryId === patientCountryId &&
      condition.followupType === FollowupType.MATERNAL &&
      condition.emergencyType === motherEmergencyType &&
      condition.isActive &&
      condition.isCondition // Only get conditions
    ) || [];
  }, [conditionsList, patientCountryId, motherEmergencyType]);

  const filteredBabyConditions = useMemo(() => {
    return conditionsList?.filter(condition =>
      condition.countryId === patientCountryId &&
      condition.followupType === FollowupType.NEONATAL &&
      (motherEmergencyType !== EmergencyType.POSTPARTUM ? condition.emergencyType === motherEmergencyType : condition.emergencyType === EmergencyType.NEONATAL) &&
      condition.isActive &&
      condition.isCondition  // Only get conditions
    ) || [];
  }, [conditionsList, patientCountryId, motherEmergencyType]);

  const filteredMotherSymptoms = useMemo(() => {
    return symptomsList?.filter(symptom =>
      symptom.country.id === patientCountryId &&
      symptom.isActive &&
      symptom.emergencyType === motherEmergencyType
    ) || [];
  }, [symptomsList, motherEmergencyType]);

  const filteredBabySymptoms = useMemo(() => {
    return symptomsList?.filter(symptom =>
      symptom.country.id === patientCountryId &&
      symptom.isActive &&
      symptom.emergencyType === EmergencyType.NEONATAL
    ) || [];
  }, [symptomsList]);

  // Dynamic next step calculation
  const calculateNextStep = (currentStepParam: number, formDataParam: FollowupFormData): number => {
    switch (currentStepParam) {
      case STEPS.DIAGNOSIS_ACCURACY.number:
        // If diagnosis not accurate, check patient type
        if (patientCaseType === CaseType.MOTHER || patientCaseType === CaseType.BOTH) {
          if (formDataParam.diagnosisOk === true) {
            return STEPS.MOTHER_ALIVE.number;
          }
          return STEPS.MOTHER_SYMPTOMS.number;
        }
        if (patientCaseType === CaseType.BABY) {
          if (formDataParam.diagnosisOk === true) {
            return STEPS.BABY_ALIVE.number;
          }
          return STEPS.BABY_SYMPTOMS.number;
        }
        return STEPS.MOTHER_SYMPTOMS.number;

      case STEPS.MOTHER_SYMPTOMS.number:
        if (patientCaseType === CaseType.BOTH) {
          return STEPS.BABY_SYMPTOMS.number;
        }
        return STEPS.MOTHER_ALIVE.number;

      case STEPS.BABY_SYMPTOMS.number:
        if (patientCaseType === CaseType.MOTHER || patientCaseType === CaseType.BOTH) {
          return STEPS.MOTHER_ALIVE.number;
        }
        return STEPS.BABY_ALIVE.number;

      case STEPS.MOTHER_ALIVE.number:
        if (isAntepartum) {
          return STEPS.MOTHER_DELIVERED.number;
        }
        return STEPS.MOTHER_FOLLOWUP_OUTCOMES.number;

      case STEPS.MOTHER_DELIVERED.number:
        return STEPS.MOTHER_FOLLOWUP_OUTCOMES.number;

      case STEPS.MOTHER_FOLLOWUP_OUTCOMES.number:
        // Show conditions if mother is NOT alive
        if (formDataParam.motherAlive === false) {
          return STEPS.MOTHER_FOLLOWUP_CONDITIONS.number;
        }
        // Skip conditions, check if baby section needed
        if (patientCaseType === CaseType.BOTH || formDataParam.isDelivered === true) {
          return STEPS.BABY_ALIVE.number;
        }
        return STEPS.ADDITIONAL_DETAILS.number;

      case STEPS.MOTHER_FOLLOWUP_CONDITIONS.number:
        if (patientCaseType === CaseType.BOTH || formDataParam.isDelivered === true) {
          return STEPS.BABY_ALIVE.number;
        }
        return STEPS.ADDITIONAL_DETAILS.number;

      case STEPS.BABY_ALIVE.number: {
        return STEPS.BABY_FOLLOWUP_OUTCOMES.number;
      }

      case STEPS.BABY_INFO.number: // Currently Disabled
        return STEPS.BABY_FOLLOWUP_OUTCOMES.number;

      case STEPS.BABY_FOLLOWUP_OUTCOMES.number: {
        // Show conditions if baby is NOT alive
        const currentBaby = formDataParam.patientBabies?.[currentBabyIndex];
        if (currentBaby?.isAlive === false) {
          return STEPS.BABY_FOLLOWUP_CONDITIONS.number;
        }
        if (isPostpartum || isNeonatal) {
          return STEPS.ADDITIONAL_DETAILS.number;
        }
        return STEPS.ADD_ANOTHER_BABY.number;
      }

      case STEPS.BABY_FOLLOWUP_CONDITIONS.number:
        if (isPostpartum || isNeonatal) {
          return STEPS.ADDITIONAL_DETAILS.number;
        }
        return STEPS.ADD_ANOTHER_BABY.number;

      case STEPS.ADD_ANOTHER_BABY.number:
        // Partly handled by handleAddAnotherBaby function for looping
        return STEPS.ADDITIONAL_DETAILS.number;

      case STEPS.ADDITIONAL_DETAILS.number:
        return STEPS.ADDITIONAL_DETAILS.number;

      default:
        return currentStepParam + 1;
    }
  };

  // Navigation functions
  const handleNextStep = () => {
    const nextStep = calculateNextStep(currentStep, formData);

    if (nextStep !== currentStep) {
      // Add current step to navigation stack
      setNavigationStack(prev => [...prev, currentStep]);
      setCurrentStep(nextStep);
    }
  };

  const handlePreviousStep = () => {
    if (navigationStack.length > 0) {
      // Pop the last step from navigation stack
      const newStack = [...navigationStack];
      const previousStep = newStack.pop();
      setNavigationStack(newStack);
      if (previousStep) {
        setCurrentStep(previousStep);
        // If going back to ADD_ANOTHER_BABY step, decrement baby index
        if (previousStep === STEPS.ADD_ANOTHER_BABY.number && currentBabyIndex > 0) {
          setCurrentBabyIndex(prev => prev - 1);
        }
      }
    }
  };

  const updateFormData = (updates: Partial<FollowupFormData>) => {
    setFormData(prev => ({...prev, ...updates}));
  };

  const handleAddAnotherBaby = (addAnother: boolean) => {
    if (addAnother && currentBabyIndex < MAX_BABIES - 1) {
      // Increment baby index and loop back to baby alive step
      setCurrentBabyIndex(prev => prev + 1);
      setCurrentStep(STEPS.BABY_ALIVE.number);
      setNavigationStack(prev => [...prev, STEPS.ADD_ANOTHER_BABY.number]);
    } else {
      // Trim any babies after the current index before proceeding
      setFormData(prev => ({
        ...prev,
        patientBabies: prev.patientBabies.slice(0, currentBabyIndex + 1)
      }));
      // Proceed to additional details
      handleNextStep();
    }
  };

  const updateCurrentBaby = (updates: Partial<IPatientBaby>) => {
    setFormData(prev => {
      const updatedBabies = [...(prev.patientBabies || [])];
      // Ensure baby exists at current index
      if (!updatedBabies[currentBabyIndex]) {
        updatedBabies[currentBabyIndex] = {} as IPatientBaby;
      }
      // Update baby at current index
      updatedBabies[currentBabyIndex] = {
        ...updatedBabies[currentBabyIndex],
        ...updates,
      };
      return {...prev, patientBabies: updatedBabies};
    });
  };

  const handleSave = (finalFormData: FollowupFormData) => {
    const followupData: FollowupFormData = {
      journeyId: journey.id,
      diagnosisOk: finalFormData.diagnosisOk === null ? true : finalFormData.diagnosisOk,
      motherSymptoms: finalFormData.motherSymptoms,
      motherSymptom: finalFormData.motherSymptom,
      babySymptoms: finalFormData.babySymptoms,
      babySymptom: finalFormData.babySymptom,
      motherAlive: finalFormData.motherAlive,
      isDelivered: finalFormData.isDelivered,
      motherFollowupOutcome: finalFormData.motherFollowupOutcome,
      motherFollowupCondition: finalFormData.motherFollowupCondition,
      babyAlive: finalFormData.babyAlive,
      babyFollowupOutcome: finalFormData.babyFollowupOutcome,
      babyFollowupCondition: finalFormData.babyFollowupCondition,
      patientBabies: finalFormData.patientBabies,
      additionalDetails: finalFormData.additionalDetails,
    };

    onSave(followupData);
  };

  const handleClose = () => {
    // Reset form data when closing
    setCurrentStep(STEPS.DIAGNOSIS_ACCURACY.number);
    setNavigationStack([]);
    setFormData({...initialFormData});
    setCurrentBabyIndex(0);
    toggle();
  };

  const canGoBack = navigationStack.length > 0;
  const canGoNext = currentStep < STEPS.ADDITIONAL_DETAILS.number;

  const renderStep = () => {
    const commonProps: StepComponentProps = {
      formData,
      updateFormData,
      onNext: handleNextStep,
      onPrevious: handlePreviousStep,
      canGoBack,
      canGoNext,
      patientCaseType,
      journey,
      motherFollowupData: filteredMotherFollowups,
      babyFollowupData: filteredBabyFollowups,
      motherSymptomsData: filteredMotherSymptoms,
      babySymptomsData: filteredBabySymptoms,
      motherConditionsData: filteredMotherConditions,
      babyConditionsData: filteredBabyConditions,
      patientInfo,
      onSave: handleSave,
    };

    switch (currentStep) {
      case STEPS.DIAGNOSIS_ACCURACY.number:
        return <STEPS.DIAGNOSIS_ACCURACY.component {...commonProps} />;
      case STEPS.MOTHER_SYMPTOMS.number:
        return <STEPS.MOTHER_SYMPTOMS.component {...commonProps} />;
      case STEPS.BABY_SYMPTOMS.number:
        return <STEPS.BABY_SYMPTOMS.component {...commonProps} />;
      case STEPS.MOTHER_ALIVE.number:
        return <STEPS.MOTHER_ALIVE.component {...commonProps} />;
      case STEPS.MOTHER_DELIVERED.number:
        return <STEPS.MOTHER_DELIVERED.component {...commonProps} />;
      case STEPS.MOTHER_FOLLOWUP_OUTCOMES.number:
        return <STEPS.MOTHER_FOLLOWUP_OUTCOMES.component {...commonProps} />;
      case STEPS.MOTHER_FOLLOWUP_CONDITIONS.number:
        return <STEPS.MOTHER_FOLLOWUP_CONDITIONS.component {...commonProps} />;
      case STEPS.BABY_ALIVE.number:
        return <STEPS.BABY_ALIVE.component {...commonProps} updateCurrentBaby={updateCurrentBaby}
                                           currentBabyIndex={currentBabyIndex}/>
      case STEPS.BABY_INFO.number:
        return <STEPS.BABY_INFO.component {...commonProps} updateCurrentBaby={updateCurrentBaby}
                                          currentBabyIndex={currentBabyIndex}/>;
      case STEPS.BABY_FOLLOWUP_OUTCOMES.number:
        return <STEPS.BABY_FOLLOWUP_OUTCOMES.component {...commonProps} updateCurrentBaby={updateCurrentBaby}
                                                       currentBabyIndex={currentBabyIndex}/>;
      case STEPS.BABY_FOLLOWUP_CONDITIONS.number:
        return <STEPS.BABY_FOLLOWUP_CONDITIONS.component {...commonProps} updateCurrentBaby={updateCurrentBaby}
                                                         currentBabyIndex={currentBabyIndex}/>;
      case STEPS.ADD_ANOTHER_BABY.number:
        return <STEPS.ADD_ANOTHER_BABY.component {...commonProps} currentBabyIndex={currentBabyIndex}
                                                 maxBabies={MAX_BABIES} onAddAnotherBaby={handleAddAnotherBaby}/>;
      case STEPS.ADDITIONAL_DETAILS.number:
        return <STEPS.ADDITIONAL_DETAILS.component {...commonProps} onSave={handleSave} updating={followupSaving}/>;
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" style={{maxWidth: '800px'}}>
      <ModalHeader toggle={handleClose}>
        <div>
          <div className="h5 mb-1">Follow-up
            on {journey.patientCase?.patient?.firstName} {journey.patientCase?.patient?.lastName}</div>
          <div className="text-muted" style={{fontSize: '14px'}}>
            {patientCaseType === CaseType.MOTHER ? 'Mother' :
              patientCaseType === CaseType.BABY ? 'Baby' :
                patientCaseType === CaseType.BOTH ? 'Mother & Baby' : 'Patient'}
            ({journey.patientCase?.type || 'Emergency'})
          </div>
        </div>
      </ModalHeader>
      <ModalBody>
        {renderStep()}
      </ModalBody>
    </Modal>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  followupList: storeState.followup.followupList,
  symptomsList: storeState.symptom.entities,
  conditionsList: storeState.condition.conditionList,
  loading: storeState.followup.loading,
  followupSaving: storeState.patientCase.followupSaving,
});

const mapDispatchToProps = {
  getFollowups,
  getSymptoms,
  getConditions,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

const FollowupModal = connect(mapStateToProps, mapDispatchToProps)(FollowupModalComponent);

export default FollowupModal;
